import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Container,
  Divider,
  alpha,
  Fade
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';
import { PenSquare, ArrowLeft, Save, Info } from 'lucide-react';
import PageHeader from './PageHeader';

export default function EditPrompt() {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const { authenticatedRequest, error: authError } = useAuth();

  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    name: '',
    prompt_text: '',
    settings: {}
  });

  useEffect(() => {
    fetchPrompt();
  }, [promptId]);

  const fetchPrompt = async () => {
    try {
      setLoading(true);
      await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        const data = await apiClient.getPrompt(promptId);
        setPrompt(data);
        setFormData({
          name: data.name,
          prompt_text: data.prompt_text,
          settings: data.settings || {}
        });
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      const result = await authenticatedRequest(async (token) => {
        apiClient.setAuthToken(token);
        return await apiClient.updatePrompt(promptId, {
          name: formData.name,
          prompt_text: formData.prompt_text,
          settings: formData.settings || {}
        });
      });
      
      // Check if the update was successful
      if (result) {
        setNotification({
          open: true,
          message: 'Prompt updated successfully',
          severity: 'success'
        });
        
        // Navigate to correct URL /prompts instead of /prompts/saved
        setTimeout(() => {
          navigate('/prompts');
        }, 800);
      } else {
        throw new Error('Update returned an invalid response');
      }
    } catch (err) {
      console.error('Error updating prompt:', err);
      setError(err.message || 'Failed to update prompt');
      setNotification({
        open: true,
        message: `Update failed: ${err.message || 'Unknown error'}`,
        severity: 'error'
      });
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 100px)',
        gap: 2
      }}>
        <CircularProgress size={40} sx={{ color: '#4158D0' }} />
        <Typography variant="body1" color="text.secondary">
          Loading prompt...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 4 } }}>
      {/* Page Header */}
      <PageHeader
        category="Prompt Lab"
        title="Edit Prompt"
        icon={
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #43A047 0%, #66BB6A 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(67, 160, 71, 0.2)',
          }}>
            <PenSquare size={24} color="#FFFFFF" strokeWidth={1.5} />
          </Box>
        }
        description="Modify your prompt template to improve its effectiveness"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          {error}
        </Alert>
      )}

      {/* Content Card */}
      <Paper 
        elevation={0}
        sx={{ 
          mt: 3,
          p: 0,
          borderRadius: '16px', 
          border: '1px solid',
          borderColor: alpha('#4158D0', 0.1),
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)'
        }}
      >
        {/* Header with actions */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha('#f8f9fc', 0.6)
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                color: '#1A2034'
              }}
            >
              {formData.name || 'Untitled Prompt'}
            </Typography>
            <Box 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                borderRadius: '20px', 
                bgcolor: alpha('#4158D0', 0.08), 
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#4158D0'
              }}
            >
              {prompt?.type || 'Custom'}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/prompts')}
              disabled={saving}
              startIcon={<ArrowLeft size={16} />}
              sx={{ 
                borderRadius: '8px',
                borderColor: alpha('#000', 0.2),
                color: 'text.secondary',
                '&:hover': {
                  borderColor: alpha('#000', 0.3),
                  bgcolor: alpha('#000', 0.05)
                }
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Save size={16} />}
              sx={{ 
                borderRadius: '8px',
                background: 'linear-gradient(90deg, #4158D0, #6A7BFF)',
                boxShadow: `0 4px 10px ${alpha('#4158D0', 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 15px ${alpha('#4158D0', 0.4)}`,
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 600,
                color: '#1A2034',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Prompt Name
            </Typography>
            <TextField
              placeholder="Give your prompt a descriptive name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              variant="outlined"
              InputProps={{
                sx: { 
                  borderRadius: '8px', 
                  bgcolor: alpha('#f5f7fa', 0.5),
                  '&:hover': {
                    bgcolor: alpha('#f5f7fa', 0.8),
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#4158D0', 0.2),
                  }
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 1.5, 
                fontWeight: 600,
                color: '#1A2034',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Prompt Content
              <Box 
                component="span" 
                sx={{ 
                  color: alpha('#4158D0', 0.7),
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.8rem',
                  fontWeight: 'normal'
                }}
              >
                <Info size={14} />
                Provide clear instructions to the AI
              </Box>
            </Typography>
            
            <TextField
              placeholder="Write your prompt instructions here..."
              value={formData.prompt_text}
              onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
              required
              multiline
              rows={16}
              fullWidth
              variant="outlined"
              sx={{ mb: 3 }}
              InputProps={{
                sx: { 
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  bgcolor: alpha('#f5f7fa', 0.5),
                  '&:hover': {
                    bgcolor: alpha('#f5f7fa', 0.8),
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha('#4158D0', 0.2),
                  }
                }
              }}
            />
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ 
            width: '100%',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}